import React from "react";
import {MenuItem} from "@mui/material";
import CCInputLabel from "../Common/CCInputLabel";
import CCSelect from "../Common/CCSelect";
import PositionTreeGuides, {ROW_PADDING} from "../Navigation/PositionTreeGuides";
import {buildPositionTree, TreeRow} from "../Navigation/positionTree";

type Entry = {USID: string; Name: string; ParentUSID?: string | null};

export type PersonaOption = {
    key: string;
    // What the caller receives on selection. An ancestor gets a value that cannot be mistaken for a
    // persona, so choosing one is impossible rather than merely discouraged.
    value: string;
    label: string;
    disabled: boolean;
    row?: TreeRow;
};

// Which options the dropdown offers, as data. Pure, so the rules that matter -- an ancestor is
// never selectable and never carries a persona value -- can be asserted without rendering MUI.
export function buildPersonaOptions(
    positions: Entry[], positionAncestors: Entry[],
    userGroups: Entry[], workGroups: Entry[], valuePrefix: string): PersonaOption[] {

    const options: PersonaOption[] = [];

    buildPositionTree(positions, positionAncestors).forEach((row, index) => {
        if (index === 0) options.push({key: "h-positions", value: "positions",
                                       label: "Positions", disabled: true});
        options.push({
            key: `p-${row.node.USID}`,
            value: row.selectable ? `${valuePrefix}Position:${row.node.USID}`
                                  : `context-${row.node.USID}`,
            label: row.node.Name,
            disabled: !row.selectable,
            row,
        });
    });

    userGroups.forEach((group, index) => {
        if (index === 0) options.push({key: "h-userGroups", value: "userGroups",
                                       label: "User Groups", disabled: true});
        options.push({key: `u-${group.USID}`, value: `${valuePrefix}UserGroup:${group.USID}`,
                      label: group.Name, disabled: false});
    });

    workGroups.forEach((group, index) => {
        if (index === 0) options.push({key: "h-workgroups", value: "workgroups",
                                       label: "Workgroups", disabled: true});
        options.push({key: `w-${group.USID}`, value: `${valuePrefix}WorkGroup:${group.USID}`,
                      label: group.Name, disabled: false});
    });

    return options;
}

// The grouped persona dropdown shared by the login screen and the SSO persona prompt. Renders the
// account's Positions / User Groups / Workgroups under disabled group headers, exactly as the login
// flow always has. Each option's value is `${valuePrefix}${Mode}:${USID}`, so the login flow (which
// prefixes the account USID and re-submits to authenticate) and the SSO flow (no prefix, applies the
// visibility client-side) both drive the same control.
//
// Positions are laid out as a tree, the same one the NavBar persona switcher draws, from the same
// builder. `positionAncestors` are the positions ABOVE the ones this account holds: they are what
// make it a tree at all, because a user's own positions rarely touch each other. They are rendered
// disabled, and carry no persona value -- they are not personas this user has, and the server would
// refuse one with CC-4425-40FC.
export default function PersonaSelect({
    positions = [],
    positionAncestors = [],
    userGroups = [],
    workGroups = [],
    value,
    onChange,
    valuePrefix = "",
    label = "Persona",
}: {
    positions?: Entry[];
    positionAncestors?: Entry[];
    userGroups?: Entry[];
    workGroups?: Entry[];
    value: string | null;
    onChange: (value: string) => void;
    valuePrefix?: string;
    label?: string;
}) {
    const options = buildPersonaOptions(positions, positionAncestors, userGroups, workGroups,
                                        valuePrefix);

    // The closed control shows a plain name. Without this it renders the chosen item's children,
    // which now include the tree guides -- a form field with an elbow line drawn into it reads as a
    // rendering fault rather than a hierarchy.
    const labels: Record<string, string> = {};
    for (const option of options) {
        if (!option.disabled) labels[option.value] = option.label;
    }

    return (
        <>
            <CCInputLabel>{label}</CCInputLabel>
            <CCSelect
                size={"small"}
                fullWidth={true}
                value={value ?? ""}
                renderValue={(selected: any) => labels[selected as string] ?? ""}
                onChange={(e: any) => onChange(e.target.value)}>
                {options.map(option => (
                    <MenuItem value={option.value} disabled={option.disabled} key={option.key}
                              sx={option.row ? {alignItems: "stretch",
                                                paddingTop: `${ROW_PADDING}px`,
                                                paddingBottom: `${ROW_PADDING}px`} : undefined}>
                        {option.row ? (
                            <>
                                &emsp;
                                <PositionTreeGuides row={option.row}/>
                                <span style={{display: "flex", alignItems: "center"}}>{option.label}</span>
                            </>
                        ) : (option.row === undefined && option.disabled
                            ? option.label
                            : <>&emsp;{option.label}</>)}
                    </MenuItem>
                ))}
            </CCSelect>
        </>
    );
}
